/**
 * Iconify JSON bundle support for uwc-icon.
 *
 * Registers a provider's complete icon set as a single JSON bundle (Iconify format).
 * After loading, uwc-icon resolves icons from the in-memory cache instead of making
 * individual HTTP requests — reducing N requests to exactly 1 per provider.
 *
 * @example
 * ```ts
 * import { registerIconBundle } from '@uwckit/components/icon';
 *
 * // Bootstrap Icons (default library)
 * registerIconBundle('default',
 *   'https://cdn.jsdelivr.net/npm/@iconify-json/bi/icons.json'
 * );
 *
 * // Material Design Icons
 * registerIconBundle('mdi',
 *   'https://cdn.jsdelivr.net/npm/@iconify-json/mdi/icons.json'
 * );
 *
 * // Font Awesome Free — one entry per style bucket
 * registerIconBundle('fontawesome-solid',
 *   'https://cdn.jsdelivr.net/npm/@iconify-json/fa-solid/icons.json'
 * );
 * registerIconBundle('fontawesome-regular',
 *   'https://cdn.jsdelivr.net/npm/@iconify-json/fa-regular/icons.json'
 * );
 * registerIconBundle('fontawesome-brands',
 *   'https://cdn.jsdelivr.net/npm/@iconify-json/fa-brands/icons.json'
 * );
 * ```
 */

// ── Iconify JSON format ───────────────────────────────────────────────────────

export interface IconifyIconData {
  /** Inner SVG content (path/group/etc. elements, no <svg> wrapper). */
  body: string;
  width?:  number;
  height?: number;
}

export interface IconifyBundle {
  prefix: string;
  icons:   Record<string, IconifyIconData>;
  /** Aliases map alias-name → parent-name. */
  aliases?: Record<string, { parent: string; body?: string; width?: number; height?: number }>;
  /** Default viewport width (used when individual icon omits `width`). */
  width?:  number;
  /** Default viewport height (used when individual icon omits `height`). */
  height?: number;
}

// ── Internal registry ─────────────────────────────────────────────────────────

interface BundleEntry {
  url:     string;
  /** name → complete `<svg>…</svg>` string ready for unsafeSVG / innerHTML */
  icons:   Map<string, string>;
  /** In-flight fetch promise (shared across concurrent callers). */
  loading: Promise<void> | null;
  loaded:  boolean;
}

const bundleRegistry = new Map<string, BundleEntry>();

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Register an Iconify JSON bundle URL for the given library name.
 * Must be called before the first uwc-icon with that library renders.
 * Safe to call multiple times for the same library — subsequent calls are no-ops.
 */
export function registerIconBundle(library: string, url: string): void {
  if (!bundleRegistry.has(library)) {
    bundleRegistry.set(library, { url, icons: new Map(), loading: null, loaded: false });
  }
}

/**
 * Fetch and parse the bundle for `library`.
 * Concurrent calls share a single in-flight request (promise-sharing).
 * Resolves immediately if already loaded.
 */
export async function loadIconBundle(library: string): Promise<void> {
  const entry = bundleRegistry.get(library);
  if (!entry)          return;
  if (entry.loaded)    return;
  if (entry.loading) { await entry.loading; return; }

  entry.loading = (async () => {
    const r    = await fetch(entry.url);
    const data: IconifyBundle = await r.json();
    _parseInto(data, entry.icons);
    entry.loaded = true;
  })();

  await entry.loading;
}

/**
 * Look up a cached SVG string for `name` in `library`.
 * Returns `undefined` if the bundle is not loaded or the icon does not exist.
 */
export function getBundledIconSvg(library: string, name: string): string | undefined {
  return bundleRegistry.get(library)?.icons.get(name);
}

/** True if `registerIconBundle` has been called for this library. */
export function isBundleRegistered(library: string): boolean {
  return bundleRegistry.has(library);
}

/** True if the bundle has been fully fetched and parsed. */
export function isBundleLoaded(library: string): boolean {
  return bundleRegistry.get(library)?.loaded ?? false;
}

/**
 * Returns all icon names present in a loaded bundle, sorted alphabetically.
 * Returns an empty array if the bundle is not loaded yet.
 */
export function getBundleIconNames(library: string): string[] {
  const entry = bundleRegistry.get(library);
  return entry?.loaded ? Array.from(entry.icons.keys()).sort() : [];
}

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Convert an Iconify JSON bundle into a flat map of  name → SVG string.
 * Resolves aliases so every name returns a complete standalone SVG.
 */
function _parseInto(data: IconifyBundle, out: Map<string, string>): void {
  const defaultW = data.width  ?? 24;
  const defaultH = data.height ?? 24;

  for (const [name, icon] of Object.entries(data.icons)) {
    const w = icon.width  ?? defaultW;
    const h = icon.height ?? defaultH;
    out.set(name, _wrapSvg(icon.body, w, h));
  }

  // Resolve aliases → copy the parent SVG string
  if (data.aliases) {
    for (const [alias, meta] of Object.entries(data.aliases)) {
      const parent = out.get(meta.parent);
      if (parent) out.set(alias, parent);
    }
  }
}

function _wrapSvg(body: string, w: number, h: number): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}">${body}</svg>`;
}
